/* LPC welcome offer (sections/lpc-welcome.liquid).
   Step 1 picks an answer (saved as a customer tag), step 2 posts the email through the theme's customer
   form, step 3 shows the code. Shopify redirects a customer-form post back to the page with
   ?customer_posted=true and keeps the form's #id, which is how the success step is recognised. */
(() => {
  if (customElements.get('lpc-welcome')) return;

  const STORE_KEY = 'lpc_welcome';
  const TAG_KEY = 'lpc_welcome_tag';
  const BASE_TAGS = 'newsletter,welcome-quiz';

  const read = (storage, key) => {
    try {
      return storage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const write = (storage, key, value) => {
    try {
      storage.setItem(key, value);
    } catch (e) {
      /* private mode: the offer just shows again next visit */
    }
  };

  class LpcWelcome extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-lpc-welcome-dialog]');
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.tagsInput = this.querySelector('[data-lpc-tags]');
      this.formId = this.dataset.formId;
      this.isOpen = false;

      this.querySelectorAll('[data-lpc-answer]').forEach((button) =>
        button.addEventListener('click', () => this.pick(button.dataset.tag))
      );
      this.querySelectorAll('[data-lpc-welcome-close]').forEach((el) =>
        el.addEventListener('click', () => this.close())
      );
      const copy = this.querySelector('[data-lpc-copy]');
      if (copy) copy.addEventListener('click', () => this.copyCode(copy));
      // On the document, not the element: right after load focus may still be on <body>.
      this.onKey = this.onKey.bind(this);

      const ownPost = location.hash === '#' + this.formId;
      if (ownPost && /[?&]customer_posted=true/.test(location.search)) {
        write(localStorage, STORE_KEY, JSON.stringify({ state: 'joined' }));
        this.show('done');
        this.open();
        return;
      }
      if (ownPost && this.querySelector('[data-lpc-welcome-error]')) {
        this.setTags(read(sessionStorage, TAG_KEY));
        this.show('email');
        this.open();
        return;
      }

      if (this.dataset.designMode === 'true') {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.section) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.section) this.close(false);
        });
        return;
      }

      if (this.dataset.subscribed === 'true' || this.suppressed()) return;
      if (/^\/(cart|checkouts?|account)(\/|$)/.test(location.pathname)) return;
      const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
      this.timer = setTimeout(() => this.open(), delay);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('keydown', this.onKey);
    }

    suppressed() {
      try {
        const saved = JSON.parse(read(localStorage, STORE_KEY) || 'null');
        if (!saved) return false;
        if (saved.state === 'joined') return true;
        return Date.now() < Number(saved.until || 0);
      } catch (e) {
        return false;
      }
    }

    setTags(tag) {
      if (!this.tagsInput) return;
      this.tagsInput.value = tag ? BASE_TAGS + ',' + tag : BASE_TAGS;
    }

    pick(tag) {
      this.setTags(tag);
      write(sessionStorage, TAG_KEY, tag || '');
      this.show('email');
      const email = this.querySelector('input[type="email"]');
      if (email) email.focus();
    }

    show(step) {
      this.steps.forEach((el) => {
        el.hidden = el.dataset.step !== step;
      });
      this.querySelectorAll('[data-step-hide]').forEach((el) => {
        el.hidden = el.dataset.stepHide === step;
      });
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.dialog.hidden = false;
      document.documentElement.classList.add('lpc-welcome-open');
      document.addEventListener('keydown', this.onKey);
      const first = this.focusable()[0];
      (first || this.dialog).focus();
    }

    close(remember = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.dialog.hidden = true;
      document.documentElement.classList.remove('lpc-welcome-open');
      document.removeEventListener('keydown', this.onKey);
      if (remember && !this.joined()) {
        const days = Math.max(1, Number(this.dataset.days) || 14);
        write(localStorage, STORE_KEY, JSON.stringify({ state: 'closed', until: Date.now() + days * 864e5 }));
      }
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    }

    joined() {
      try {
        return JSON.parse(read(localStorage, STORE_KEY) || 'null')?.state === 'joined';
      } catch (e) {
        return false;
      }
    }

    focusable() {
      return [...this.dialog.querySelectorAll('button, a[href], input:not([type="hidden"])')].filter(
        (el) => !el.closest('[hidden]') && !el.disabled
      );
    }

    onKey(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = this.focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    copyCode(button) {
      const code = this.querySelector('[data-lpc-code]');
      if (!code || !navigator.clipboard) return;
      navigator.clipboard.writeText(code.textContent.trim()).then(() => {
        const label = button.textContent;
        button.textContent = button.dataset.copied || 'Copied';
        setTimeout(() => {
          button.textContent = label;
        }, 2000);
      });
    }
  }

  customElements.define('lpc-welcome', LpcWelcome);
})();
